import { Grid } from '#~/control/components/grid-lines/index.js';
import {
  FastFrameSelectStage,
  useFastFrameSelectStage
} from '#~/control/components/interaction/fast-frame/index.js';
import { Interaction } from '#~/control/components/interaction/index.js';
import { useGridLinesPref, useToolPref } from '#~/control/hooks.js';
import { GridLines, Tool } from '#~/control/index.js';
import { useCalibration, useDeviceIds, useRoom, useSelectedVideo } from '#~/room/hooks.js';
import { isTrialCameraLocked, useTrialCameraOrder } from '#~/api/billing/camera-access.js';
import { useSettings } from '#~/room/components/video/hooks.js';
import { useGatedVideoIds } from '#~/room/use-gating.js';
import { Viewer } from '#~/video/components/viewer/index.js';
import { Arrangement } from '#~/video/index.js';
import { Annotation } from './annotation.jsx';
import { GutterSize, VisibleColumns } from './constants.js';
import { EmptyState } from './empty-state.jsx';
import { LockedCameraNotice } from './locked-camera-notice.jsx';
import { useLayout } from './hooks.js';
import type { Props } from './types.js';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import { isCalibrating } from '@studio/models/control/calibration';
import { toDeviceId } from '@studio/models/device';
import { χref } from '@studio/react-utils/hooks';
import { useEffect, useMemo, useRef } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { twMerge } from 'cn';

/** `<Content>` component. */
export const Content = ({ className, canControl, ...props }: Props): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const layout = useLayout(containerRef);
  const [gridLines] = useGridLinesPref();
  const [selectedId, setSelectedId] = useSelectedVideo();
  // Empty-studio signal — `useLayout` returns `undefined` both when
  // there are zero devices AND when the canvas hasn't been measured
  // yet, so we can't rely on `!layout` alone. `useDeviceIds` gives an
  // unambiguous "the studio has no video sources" check.
  const { videoIds } = useDeviceIds();
  const roomId = useRoom((room) => room.id);
  const devices = useRoom((room) => room.devices);
  const trialCameraOrder = useTrialCameraOrder(roomId);
  const availableIdentity = trialCameraOrder[0];
  const candidate = availableIdentity ? devices[toDeviceId(availableIdentity.deviceId)] : undefined;
  const availableCamera = candidate?.agentId === availableIdentity?.agentId ? candidate : undefined;
  const lockedVideoIds = useMemo(
    () =>
      new Set(
        trialCameraOrder.flatMap((camera) => {
          const id = toDeviceId(camera.deviceId);
          return devices[id]?.agentId === camera.agentId &&
            isTrialCameraLocked(roomId, camera.agentId, id)
            ? [id]
            : [];
        })
      ),
    [roomId, devices, trialCameraOrder]
  );
  const fastFrameSelectStage = useFastFrameSelectStage();
  const [currentTool] = useToolPref();
  const calibration = useCalibration(selectedId);
  // Gating §6 — set of video ids that must NOT receive control affordances:
  // transitory placeholders + committed devices whose stream auth is INVALID.
  // The canvas <Interaction> layer (pointer-drag PTZ + on-screen joystick)
  // is suppressed for these tiles so movement cannot be driven before auth.
  const gatedVideoIds = useGatedVideoIds();
  // Own the Advanced Settings dialog HERE, in the stable <Content> parent —
  // NOT inside the per-tile <Annotation> overlay. A settings commit does a
  // remove+re-add of the device (settings/utils.ts replaceDevice); on a
  // single-source studio that briefly drops videoIds to 0, unmounting the tile,
  // and an <Annotation>-owned dialog would be torn down with it (the multi-add
  // regression where Save closed the settings window). Owning it here and
  // threading the opener down to each <Annotation> keeps the dialog alive across
  // that churn. Per `reference-rules-in-comments` (ai.knowledge/global).
  const openSettings = useSettings();

  const computedGridLines =
    fastFrameSelectStage === FastFrameSelectStage.CONFIRM ? GridLines.None : gridLines;

  useEffect(() => {
    const container = χref(containerRef);

    // Scroll to the selected device
    if (!selectedId || !container) {
      return;
    }

    const target = container.querySelector(`[data-device="${selectedId}"]`);

    if (target) {
      const { left, width } = target.getBoundingClientRect();
      const { left: containerLeft, width: containerWidth } = container.getBoundingClientRect();
      const gutter = GutterSize * 2;
      const offset = left - containerLeft - (containerWidth - width) / 2 + gutter;

      container.scrollTo({ behavior: 'smooth', left: offset });
    }
  }, [selectedId]);

  const handleClick = (event: MouseEvent): void => {
    const target = event.currentTarget as HTMLElement;

    if (target.dataset.device) {
      setSelectedId(toDeviceId(target.dataset.device));
    }
  };

  return (
    <div
      className={twMerge(
        'relative h-full overflow-y-hidden',
        layout?.arrangement === Arrangement.Multiple && layout.columns.count > VisibleColumns
          ? 'overflow-x-auto'
          : 'overflow-x-hidden',
        className
      )}
      ref={containerRef}
      {...props}
    >
      {/*
        Empty-studio welcome card — Multi-Add Phase 2 W4 (re-located
        per engineer feedback 2026-05-13). Renders centered in the
        canvas when the room has zero video sources; matches PRD §14
        frame `17434-14926`. Once a source is added, videoIds populates
        and the AnimatePresence path below takes over.
        Per `reference-rules-in-comments` (ai.knowledge/global).
      */}
      {videoIds.length === 0 && <EmptyState />}

      <AnimatePresence>
        {layout?.slots.map((slot) => {
          const { deviceId, style } = slot;
          const { arrangement, columns } = layout;
          const isSelected = selectedId === deviceId;
          const isMultiView = arrangement !== Arrangement.Single;
          const isSelectable = isMultiView && !isSelected;

          return (
            <motion.div
              className={twMerge(
                'group absolute outline outline-secondary transition-[outline-color] hover:outline hover:outline-muted [&>*]:absolute',
                isSelected && isMultiView && 'outline-primary',
                isSelectable && 'cursor-pointer'
              )}
              data-device={deviceId}
              key={`motion_div_${deviceId}`}
              layout
              onClick={isSelectable ? handleClick : undefined}
              style={style}
              {...Motions.Fade}
            >
              <Viewer
                className='inset-0'
                deviceId={deviceId}
                lockedFallback={
                  <LockedCameraNotice
                    deviceId={deviceId}
                    availableCamera={availableCamera}
                    onSelect={setSelectedId}
                  />
                }
              />
              {/* Annotation overlay: name + status badge + calibration
                  indicator + needs-attention chip ("Authenticate" /
                  "Connect controls"). Was previously gated on
                  `isMultiView` so the chip never appeared on the single-
                  view main tile — the only chip surface in that mode
                  was the sidebar `<Item>`, which doesn't match the
                  Figma reference. Per Multi-Add Phase 1 manual QA
                  (Bug 7): render Annotation unconditionally so the
                  chip + name overlay reach every visible main-grid
                  tile regardless of arrangement. */}
              <Annotation deviceId={deviceId} openSettings={openSettings} />
              {canControl &&
                // A locked preview shows its explanation instead of an invisible
                // control layer that could cover the available-camera button.
                !lockedVideoIds.has(deviceId) &&
                /* Gating §6 — no PTZ/joystick interaction over a gated tile
                   (transitory placeholder or INVALID auth). The Annotation
                   above (name + "Authenticate" chip, W3) still renders — only
                   the control layer is suppressed. */
                !gatedVideoIds.has(deviceId) &&
                (!!isSelected || currentTool === Tool.Tracking) &&
                !isCalibrating(calibration) && (
                  <>
                    <Grid
                      className='inset-0 opacity-25 transition-opacity group-hover:opacity-50'
                      gridLines={computedGridLines}
                    />
                    <Interaction
                      className='inset-0'
                      deviceId={deviceId}
                      key={`interaction_${deviceId}`}
                    />
                  </>
                )}
              {
                /* Scroll Shim - https://bit.ly/3JshFNM */
                columns.count > columns.visible &&
                  slot.rowIndex === 0 &&
                  slot.columnIndex === columns.count - 1 && (
                    <div
                      style={{
                        height: `${GutterSize}px`,
                        right: `-${GutterSize}px`,
                        width: `${GutterSize}px`
                      }}
                    />
                  )
              }
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
